import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { rm } from "@/lib/format";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";

export function PageHeader({ title, subtitle, actions, testid }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6" data-testid={testid}>
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1 max-w-2xl">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function KpiCard({ label, value, sub, accent = "slate", isMoney = true, testid }) {
  const accents = {
    slate: "text-slate-900", blue: "text-blue-700", green: "text-emerald-600",
    amber: "text-amber-600", red: "text-red-600",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4 h-full" data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">{label}</div>
      <div className={`text-2xl md:text-3xl font-black tracking-tight mt-2 ${accents[accent]}`}>
        {isMoney ? rm(value) : value}
      </div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

// status: idle | saving | saved | failed
export function SaveStatus({ status, error }) {
  if (status === "saving") return <span className="text-xs text-slate-500 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Saving…</span>;
  if (status === "saved") return <span className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Saved</span>;
  if (status === "failed") return <span className="text-xs text-red-600 flex items-center gap-1"><AlertCircle className="h-3 w-3" /> {error || "Failed"}</span>;
  return null;
}

export function LoadingButton({ loading, children, ...props }) {
  return (
    <Button disabled={loading || props.disabled} {...props}>
      {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
      {children}
    </Button>
  );
}

export function BranchSelect({ value, onChange, allowAll = false, activeOnly = false, testid, placeholder = "Select branch" }) {
  const { branches } = useAuth();
  const list = activeOnly ? branches.filter((b) => b.active) : branches;
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger className="w-full" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {allowAll && <SelectItem value="__all__">All Branches</SelectItem>}
        {list.map((b) => (
          <SelectItem key={b.id} value={b.id}>{b.name} {b.active ? "" : "(inactive)"}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function branchName(branches, id) {
  if (!id) return "Unassigned";
  const b = branches.find((x) => x.id === id);
  return b ? b.name : "Unassigned";
}

export function Empty({ children }) {
  return <div className="text-center py-12 text-slate-400 text-sm" data-testid="empty-state">{children}</div>;
}

export { rm };
